import { createFileRoute } from "@tanstack/react-router"
import { addYears, subYears } from "date-fns"
import { useMemo } from "react"
import { Info } from "@phosphor-icons/react"

import VariantGraph from "~/components/product/VariantGraph"
import VariantTableCard from "~/components/product/VariantTableCard/VariantTableCard"
import { VariantsTableRow } from "~/components/product/VariantTableCard/types"
import PerformanceSummaryCards from "~/components/product/performance/PerformanceSummaryCards"
import TopBottomPerformersCard from "~/components/product/performance/TopBottomPerformersCard"
import { Alert, AlertDescription, AlertTitle } from "~/components/reusable/Alert"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import { useVariantContext } from "~/context/VariantProvider"
import { usePageTracking } from "~/hooks/usePageTracking"
import { usePerformanceData } from "~/hooks/usePerformanceData"
import { buildLookbackPhrase, LookbackTooltipBody } from "~/utils/performanceText"
import { useSkuStore } from "~/providers/sku-store-provider"
import { roundForecast } from "~/utils/formatting"
import { useChartSeriesConfig } from "~/constants/colors"
import { formatName } from "~/utils/namingUtils"
import { startOfTodayUTC } from "~/utils/datetime"
import type { DateChartSeries } from "~/components/reusable/graphs/SuperChart"
import type { ProductTableData } from "~/components/product/VariantTableCard/types"
import { AnalysisPage } from "~/components/design-system/pages"
import VariantInspector from "~/components/product/VariantInspector"

export const Route = createFileRoute("/_auth/product/$productId/_layout/performance")({
  component: RouteComponent,
})

function RouteComponent() {
  usePageTracking("Product - Performance")
  return <PerformanceTab />
}

function PerformanceTab() {
  const { productId } = Route.useParams()
  const { variants, startDate, endDate, isMonthly } = useVariantContext()
  const variantIds = useMemo(() => variants.map((v) => v.variantId), [variants])
  const warehouseId = useSkuStore((s) => s.warehouseId)
  const channel = useSkuStore((s) => s.channel)
  const chartConfig = useChartSeriesConfig()

  const effectiveStart = startDate ?? subYears(startOfTodayUTC(), 1)
  const effectiveEnd = endDate ?? addYears(startOfTodayUTC(), 1)
  // Cap at today so the table doesn't render empty future columns — the graph
  // does the same via `mode="performance"`.
  const today = startOfTodayUTC()
  const tableEndDate = effectiveEnd > today ? today : effectiveEnd

  const { summary, variantStats, expectedDemandMap, salesMap, lookback, isLoading, hasData } =
    usePerformanceData({
      variantIds,
      warehouseId,
      startDate: effectiveStart,
      endDate: effectiveEnd,
      monthlyView: isMonthly ?? false,
    })

  const lookbackPhrase = useMemo(() => buildLookbackPhrase(lookback), [lookback])
  const lookbackTooltip = lookbackPhrase ? (
    <InfoTooltip
      className="h-4 w-4"
      body={<LookbackTooltipBody min={lookback.min} max={lookback.max} />}
    />
  ) : null

  const expectedDemandSeries = useMemo<DateChartSeries | null>(() => {
    if (expectedDemandMap.size === 0) return null
    return {
      name: "Expected demand",
      data: [...expectedDemandMap.entries()]
        .sort(([a], [b]) => a - b)
        .map(([ms, qty]) => ({
          milliseconds: ms,
          value: qty != null ? roundForecast(qty) : null,
        })),
      type: "spline",
      colors: [chartConfig.demand.color],
      lineWidth: 3,
      dashStyle: chartConfig.demand.dashStyle,
    }
  }, [expectedDemandMap, chartConfig])

  // The Performance tab is currently the only consumer of the Performance index
  // and Expected demand rows, so we build the cell data inline and inject it via
  // VariantTableCard's `extraRowData` prop. If a second consumer ever needs these
  // rows (export, dashboard mini-table, etc.), extract this into a shared helper
  // alongside `usePerformanceData` (e.g. `buildPerformanceExtraRowData`) so the
  // sales / expected-demand derivation stays in one place.
  const extraRowData = useMemo<Map<string, Map<number, ProductTableData>>>(() => {
    const performanceIndexCells = new Map<number, ProductTableData>()
    for (const [ms, expected] of expectedDemandMap) {
      const sales = salesMap.get(ms)
      if (sales == null || expected == null || expected === 0) {
        performanceIndexCells.set(ms, { value: null })
        continue
      }
      performanceIndexCells.set(ms, { value: (sales / expected) * 100 })
    }

    const expectedDemandCells = new Map<number, ProductTableData>()
    for (const [ms, qty] of expectedDemandMap) {
      expectedDemandCells.set(ms, { value: qty != null ? roundForecast(qty) : null })
    }

    return new Map<string, Map<number, ProductTableData>>([
      [VariantsTableRow.PerformanceIndex, performanceIndexCells],
      [VariantsTableRow.ExpectedDemand, expectedDemandCells],
    ])
  }, [expectedDemandMap, salesMap])

  return (
    <AnalysisPage inspector={<VariantInspector sections={["image", "details", "comments"]} />}>
      <div className="flex flex-col gap-4 min-h-full">
        {channel && (
          <Alert variant="info">
            <Info size={20} />
            <AlertTitle>Performance is shown across all channels</AlertTitle>
            <AlertDescription>
              Channel-level performance isn&apos;t supported yet. The numbers below ignore the
              current channel filter.
            </AlertDescription>
          </Alert>
        )}

        <PerformanceSummaryCards
          summary={summary}
          lookbackTooltip={lookbackTooltip}
          lookbackPhrase={lookbackPhrase}
        />

        {/* Channel is intentionally omitted from the graph and table below —
          performance metrics are not available at channel level (see alert
          above). Passing `channel` would silently filter sales while expected
          demand stayed channel-agnostic, producing misleading numbers. */}
        <VariantGraph
          warehouseId={warehouseId}
          graphHeight={320}
          netSalesMode="off"
          mode="performance"
          expectedDemandSeries={expectedDemandSeries}
          titleTooltip={lookbackTooltip}
        />

        {/* In autoHeight mode AG Grid sets `min-height: var(--ag-auto-height-min-body-height)`
          on .ag-grid-scrolling-rows; with only 3 rows this leaves 40+ px of empty
          space inside the table card. Strip the min so the card hugs the row content.
          (v36 renamed ag-center-cols-* to ag-grid-scrolling-*.) */}
        <div className="[&_.ag-grid-scrolling-rows]:!min-h-0">
          <VariantTableCard
            variantIds={variantIds}
            monthlyView={isMonthly}
            startDate={effectiveStart}
            endDate={tableEndDate}
            warehouseId={warehouseId}
            rows={[
              VariantsTableRow.PerformanceIndex,
              VariantsTableRow.Sales,
              VariantsTableRow.ExpectedDemand,
            ]}
            extraRowData={extraRowData}
          />
        </div>

        {variantIds.length > 1 && (
          <TopBottomPerformersCard productId={productId} variantStats={variantStats} />
        )}

        {!isLoading && !hasData && (
          <div className="paragraph-base text-fg-subtle px-4 py-3 rounded-md border border-base bg-base">
            No overlapping historic forecast and {formatName("sale", true)} data are available for
            the selected variant{variantIds.length === 1 ? "" : "s"} yet. Performance metrics will
            appear once data accumulates.
          </div>
        )}
      </div>
    </AnalysisPage>
  )
}
